import { EmptyArray, groupBy, last, max, min, sortBy, sum } from 'lib/array/arrayUtils'
import { ITEM_STATUS_CANCELLED } from 'constants/cart'
import {
  isAccommodationItem,
  isAccommodationV2AccommodationOfferView,
  isAccommodationV2Item,
  isBNBLLEHotelItem,
  isBedbankItem,
  isCruiseItem,
  isInstantBookingLEHotelItem,
  isLEHotelItem,
  isTourV1Item,
  isTourV2Item,
  isInstantLEHotelPostPurchaseItem,
  isBNBLHotelSetDatesItem,
  findPostPurchaseCheckout,
  isVillaItem,
} from 'lib/checkout/checkoutUtils'
import { createSelector } from 'reselect'
import { excludeNullOrUndefined } from 'checkout/utils'
import { getBundledFlightTotals } from 'checkout/selectors/payment/flights'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import {
  LE_CONTRACTED_HOTEL_TYPES,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
} from 'constants/offer'
import { getCruiseBreakdownView } from 'checkout/lib/utils/cruises/view'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getCheckoutCruiseOfferView } from './cruise'
import { getHasPendingTourV2PackageOrRoomSelection, getTourV2BreakdownView } from './toursv2'
import { getBundledFlightPrice, getHotelBreakdownView } from './hotels'

import { getFlightItems, getFlightLuxPlusRegularPrice } from './flights'
import {
  CHECKOUT_ITEM_TYPE_TOUR_V2,
} from 'constants/checkout'
import { CheckoutPageId } from 'checkout/constants/pages'
import { countOccupantsForFlights, countTravellersAsOccupants, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import getTransferItemsView from 'checkout/selectors/view/itemViews/getTransferItemsView'
import { dateFormattingLocaleFromGeo, diffInDays } from 'lib/datetime/dateUtils'
import { getDefaultAirport } from 'selectors/flightsSelectors'
import { isBedbankOffer, isLEOffer } from 'lib/offer/offerTypes'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import getBedbankItemViews from './itemViews/getBedbankItemViews'
import getAccommodationV2ItemViews from './itemViews/getAccommodationV2ItemViews'
import getBedbankReduceState from './items/getBedbankReduceState'
import getTourV2ItemViews from './itemViews/getTourV2ItemViews'
import getHotelItemViews from './itemViews/getHotelItemViews'
import getTourV1ItemViews from './itemViews/getTourV1ItemViews'
import getExperienceItemsView from './itemViews/getExperienceItemsView'
import getExperienceItems from './items/getExperienceItems'
import getFlightBundledItemIds from './getFlightBundledItemIds'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { buildBundledExperienceTotalsByParentItemId } from 'checkout/lib/utils/accommodation/bundledExperienceTotals'
import isAuDomesticAirportRoute from 'lib/flights/isAuDomesticAirportRoute'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, maxFlightPassengerCount } from 'constants/flight'

// will exempt this as part of change as it has tour optional experiences
const isPostPurchaseAncillaryPayment = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (postPurchase) => postPurchase === 'deposit-balance' ||
    postPurchase === 'instalment-balance' ||
    postPurchase === 'instalment' ||
    postPurchase === 'reserve-balance' ||
    postPurchase === 'reserve-for-zero-deposit' ||
    postPurchase === 'tour-optional-experiences' ||
    postPurchase === 'tour-accommodation' ||
    postPurchase === 'payment-schedule-balance',
)

const hidePrices = createSelector(
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (state: App.State) => state.checkout.cart.mode === 'tour-change-dates',
  (state: App.State) => state.router.location.pathname,
  // Hide bedbank reduce prices until the shorter stay is session-priced.
  (state: App.State) => getBedbankReduceState(state).pendingPrice,
  (isAncillaryPayment, isPostPurchaseTourDateChange, pathname, bedbankReducePendingPrice) =>
    isAncillaryPayment || isPostPurchaseTourDateChange || last(pathname.split('/')) === CheckoutPageId.LuxPlus || bedbankReducePendingPrice,
)

export function checkoutAccommodationGroupingKey(item: App.Checkout.AccommodationItem): string {
  if (isBNBLLEHotelItem(item)) {
    return `${item.offerId}-bnbl-${item.duration}`
  }

  else if (isTourV2Item(item)) {
    return `${item.offerId}-${item.purchasableOption.fkDepartureId}`
  }

  else if (isCruiseItem(item)) {
    return `${item.offerId}-${item.departureId}`
  }

  else if (item.itemType === 'tourV1') {
    return `${item.offerId}-${item.startDate}-${item.endDate}`
  }

  else {
    return `${item.offerId}-${item.checkIn}-${item.checkOut}`
  }
}

const getCartAccommodationItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.AccommodationItem> => items.filter(isAccommodationItem),
  reselectShallowEqual,
)

const getOrderAccommodationItems = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.cart.mode,
  (existingOrder, cartMode): Array<App.Checkout.AccommodationItem> => {
    if (existingOrder) {
      return [
        ...existingOrder.items.flatMap<App.Checkout.AccommodationItem>((item) => {
          if (item.status !== ITEM_STATUS_CANCELLED && 'roomRateId' in item.package && item.reservationType === 'instant_booking' && item.reservation) {
            return [{
              itemId: item.id,
              transactionKey: item.transactionKey,
              offerId: item.offer.id,
              duration: item.duration || 0,
              itemType: 'hotel',
              packageId: item.package.id,
              roomRateId: item.package.roomRateId,
              reservationType: item.reservationType,
              checkIn: item.reservation.startDate,
              checkOut: item.reservation.endDate,
              occupancy: {
                adults: item.reservation.adults,
                teenagers: item.reservation.teenagers,
                children: item.reservation.children,
                infants: item.reservation.infants,
                childrenAge: item.reservation.childrenAge,
              },
            }]
          } else {
            return []
          }
        }),
        ...existingOrder.bedbankItems.flatMap<App.Checkout.BedbankHotelItem>((item) => {
          return item.rooms.map(room => ({
            itemId: item.id,
            sessionId: '',
            roomId: item.roomTypeId,
            checkIn: moment(item.checkIn).format(ISO_DATE_FORMAT),
            checkOut: moment(item.checkIn).format(ISO_DATE_FORMAT),
            roomRateId: item.roomRateId,
            isFlightBundle: room.isFlightBundle,
            bedGroupId: '',
            transactionKey: item.transactionKey,
            offerId: item.offer.id,
            duration: item.duration,
            newPrice: item.total,
            itemType: 'bedbankHotel',
            occupancy: room.occupancy,
          }))
        }),
        ...existingOrder.tourItems.flatMap<App.Checkout.TourV2Item>(item => {
          const isPostPurchaseTourOptionalExperience = cartMode === 'tour-optional-experiences'
          const travellersByRooms = Array.from(groupBy(
            item.tour.travellers,
            (traveller) => traveller.roomNumber,
          ).values())
          return travellersByRooms.map((travellers: Array<App.Tours.TourV2OrderItemTravellerDetails>, index: number) => {
            // as tour item is the total of all travellers, we only want to show the total price on the first item
            const total = index === 0 ? item.total : 0
            return {
              startDate: item.tour.startDate,
              endDate: item.tour.endDate,
              occupancy: countTravellersAsOccupants(travellers),
              duration: item.tour.duration,
              image: item.tour.images?.[0] ?? {},
              itemId: item.id,
              itemType: CHECKOUT_ITEM_TYPE_TOUR_V2,
              offerId: item.tourId,
              transactionKey: item.transactionKey,
              purchasableOption: {
                fkDepartureId: item.departureId,
                fkSeasonId: item.tour.seasonId,
                fkTourId: item.tourId,
                roomType: travellers[0].roomType as App.Tours.V2OfferRoomType,
                fkVariationId: item.tourOptionId ?? '',
                inventoryLeft: 0, // not stored on the order item
                fullPrice: isPostPurchaseTourOptionalExperience ? 0 : total,
                price: isPostPurchaseTourOptionalExperience ? 0 : total,
                memberPrice: isPostPurchaseTourOptionalExperience ? 0 : item.memberPrice,
                packageType: item.packageOption?.packageOption,
              },
            } as App.Checkout.TourV2Item
          })
        }),
      ]
    }

    return []
  },
)
export const getAccommodationItems = createSelector(
  (state: App.State) => getCartAccommodationItems(state),
  (state: App.State) => getOrderAccommodationItems(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (accommodationItemsFromCart, accommodationItemsFromOrder, isAncillaryPayment): Array<App.Checkout.AccommodationItem> => {
    return isAncillaryPayment ? accommodationItemsFromOrder : accommodationItemsFromCart
  },
)

export const getTourV2CheckoutItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<App.Checkout.TourV2Item> => items.filter(isTourV2Item),
)

export const getLeAccommodationItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<App.Checkout.LEHotelItem> => items.filter(isLEHotelItem),
  reselectShallowEqual,
)

export const getLeInstantPostPurchaseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InstantBookingLEHotelPostPurchaseItem> => items.filter(isInstantLEHotelPostPurchaseItem),
  reselectShallowEqual,
)

export const getBNBLHotelPostPurchaseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.BNBLLEHotelSetDatesItem> => items.filter(isBNBLHotelSetDatesItem),
  reselectShallowEqual,
)

export const getLeInstantBookingItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InstantBookingLEHotelItem> => items.filter(isInstantBookingLEHotelItem),
  reselectShallowEqual,
)

export type AddFlightsAccommodationItem = App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem | App.Checkout.AccommodationV2Item

export function isAddFlightsAccommodationItem(item: App.Checkout.AnyItem): item is AddFlightsAccommodationItem {
  return isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isAccommodationV2Item(item)
}

export const getAddFlightsAccommodationItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<AddFlightsAccommodationItem> => items.filter(isAddFlightsAccommodationItem),
  reselectShallowEqual,
)

export const checkoutAccommodationOfferView = createSelector(
  (state: App.State) => getCheckoutCruiseOfferView(state),
  (state: App.State) => getBedbankItemViews(state),
  (state: App.State) => getAccommodationV2ItemViews(state),
  (state: App.State) => getTourV2ItemViews(state),
  (state: App.State) => getHotelItemViews(state),
  (state: App.State) => getTourV1ItemViews(state),
  (
    cruiseView,
    bedbankItemViews,
    accommodationV2ItemViews,
    tourV2ItemViews,
    hotelItemViews,
    tourV1ItemViews,
  ): App.WithDataStatus<Array<App.Checkout.AccommodationOfferView>> => {
    return {
      hasRequiredData: tourV1ItemViews.hasRequiredData &&
        cruiseView.hasRequiredData &&
        bedbankItemViews.hasRequiredData &&
        accommodationV2ItemViews.hasRequiredData &&
        tourV2ItemViews.hasRequiredData &&
        hotelItemViews.hasRequiredData,
      data: [
        ...hotelItemViews.data,
        ...tourV1ItemViews.data,
        ...bedbankItemViews.data,
        ...accommodationV2ItemViews.data,
        ...cruiseView.data,
        ...tourV2ItemViews.data,
      ].filter(excludeNullOrUndefined),
    }
  },
)

export const getAccommodationBreakdownView = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getBundledFlightTotals(state),
  (state: App.State) => getFlightLuxPlusRegularPrice(state),
  (state: App.State) => hidePrices(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getHasLuxPlusOrSubscriptionInCart(state),
  (state: App.State) => state.checkout.commissionData,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getHasPendingTourV2PackageOrRoomSelection(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getTransferItemsView(state),
  (state: App.State) => state.geo,
  (
    offerViewsWithStatus,
    flightsTotals,
    flightLuxPlusRegularPrice,
    hidePrice,
    postPurchase,
    hasLuxPlusOrSubscriptionInCart,
    commissionData,
    flightItems,
    journeysById,
    experienceItems,
    hasPendingTourV2PackageOrRoomSelection,
    experienceItemsView,
    transferItemsView,
    geo,
  ): App.WithDataStatus<{ tours: Array<App.Checkout.PriceBreakdownView>, accommodations: Array<App.Checkout.PriceBreakdownView> }> => {
    if (!offerViewsWithStatus.hasRequiredData) { return { hasRequiredData: false, data: { accommodations: EmptyArray, tours: EmptyArray } } }

    const dateFormattingLocale = dateFormattingLocaleFromGeo(geo)

    const bundledExperienceTotalsByParentItemId = buildBundledExperienceTotalsByParentItemId(experienceItemsView.data, transferItemsView.data)
    for (const item of experienceItems) {
      if (!item.isAccomBundle) continue
      for (const parentItemId of item.bundledItemIds ?? []) {
        if (bundledExperienceTotalsByParentItemId[parentItemId]) continue
        bundledExperienceTotalsByParentItemId[parentItemId] = { price: 0, memberPrice: 0 }
      }
    }

    const { accommodations, tours } = offerViewsWithStatus.data.reduce<{ accommodations: Array<App.Checkout.PriceBreakdownView>, tours: Array<App.Checkout.PriceBreakdownView> }>((acc, offerView) => {
      switch (offerView.offerType) {
        case OFFER_TYPE_CRUISE:
          acc.accommodations.push(getCruiseBreakdownView(getCurrentIntl(), offerView, hidePrice))
          break
        case OFFER_TYPE_TOUR_V2:
          acc.tours.push(getTourV2BreakdownView(offerView, hidePrice || hasPendingTourV2PackageOrRoomSelection, postPurchase, commissionData, dateFormattingLocale))
          break
        case OFFER_TYPE_ALWAYS_ON:
        case OFFER_TYPE_TOUR: // Not enabled yet
        case OFFER_TYPE_BED_BANK:
        case OFFER_TYPE_HOTEL:
          acc.accommodations.push(getHotelBreakdownView(
            offerView,
            flightsTotals,
            hidePrice,
            hasLuxPlusOrSubscriptionInCart,
            commissionData,
            {
              flightItems,
              journeysById,
              flightLuxPlusRegularPrice: flightLuxPlusRegularPrice.data,
              bundledExperienceTotalsByParentItemId,
              dateFormattingLocale,
            },
          ))
          break
      }
      return acc
    }, { accommodations: [], tours: [] })

    return {
      hasRequiredData: true,
      data: {
        accommodations: sortBy(accommodations, view => view.startDate, 'asc'),
        tours,
      },
    }
  },
)

function isLEContractedHotelBreakdownView(view: App.Checkout.PriceBreakdownView): boolean {
  return !!view.productType && LE_CONTRACTED_HOTEL_TYPES.includes(view.productType)
}

/**
 * Bedbank-served stays - `bedbankHotel` items and bedbank-backed accommodationV2 items, which both
 * surface as `OFFER_TYPE_BED_BANK` - only belong to the hotel + flights package when a flight is
 * bundled with them. Matches `isBundled` on the offer view, which `getHotelBreakdownView` uses to
 * decide whether to fold the flight price into the row.
 */
function isFlightBundledBedbankBreakdownView(
  view: App.Checkout.PriceBreakdownView,
  flightBundledItemIds: Set<string>,
): boolean {
  return view.productType === OFFER_TYPE_BED_BANK &&
    view.items.some(item => !!item.itemId && flightBundledItemIds.has(item.itemId))
}

export const getHotelTotalPrice = createSelector(
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => getBundledFlightTotals(state),
  (state: App.State) => getFlightLuxPlusRegularPrice(state),
  (state: App.State) => getFlightBundledItemIds(state),
  ({ data: { accommodations } }, flightsTotal, flightLuxPlusRegularPrice, flightBundledItemIds) => {
    // Remove exactly the flight component `getHotelBreakdownView` folded in - the regular
    // (non-member) price whenever the bundle carries a LuxPlus flight discount. Subtracting
    // the member price instead leaves that discount inside the hotel value, and every
    // bundled flight tile then prices the package too high by the flight's LuxPlus saving.
    const bundledFlightPrice = getBundledFlightPrice(flightsTotal.data.price, flightLuxPlusRegularPrice.data)

    // Every flight-bundled bedbank row folds in the whole flight price, so each gives it back.
    const bedbankHotelPrice = sum(
      accommodations.filter(view => isFlightBundledBedbankBreakdownView(view, flightBundledItemIds)),
      view => view.price - bundledFlightPrice,
    )

    const hotelAccommodations = accommodations.filter(isLEContractedHotelBreakdownView)

    // No LE contracted hotel in the cart means there's no LE hotel-only portion to isolate.
    if (hotelAccommodations.length === 0) {
      return bedbankHotelPrice
    }

    return sum(hotelAccommodations, view => view.price) - bundledFlightPrice + bedbankHotelPrice
  })

/**
 * Fees charged on top of the breakdown price of every hotel item in the package - LE contracted
 * hotels, and bedbank-served stays bundled with a flight: taxes and fees, which
 * `getHotelBreakdownView` subtracts out of the row, and property fees, which it never adds.
 * `getCheckoutTotal` includes both (`price + surcharge + sum(otherFees)`), so both belong in any
 * figure meant to match the summary `Total`.
 *
 * Read from the item view totals rather than the breakdown items because a BNBL row groups
 * several items into one and carries no `taxesAndFees` of its own.
 */
export const getHotelFeesOnTopOfBreakdownPrice = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  ({ data: offerViews }): number => {
    return sum(offerViews.flatMap((view) => {
      const isLEContractedHotel = view.offerType === OFFER_TYPE_HOTEL || view.offerType === OFFER_TYPE_ALWAYS_ON
      const isFlightBundledBedbank = view.offerType === OFFER_TYPE_BED_BANK && !!view.isBundled
      if (!isLEContractedHotel && !isFlightBundledBedbank) {
        return []
      }

      return view.itemViews.map(itemView =>
        (itemView.totals.taxesAndFees ?? 0) + (itemView.totals.otherFees?.propertyFees ?? 0),
      )
    }))
  })

/**
 * Hotel-only portion of a bundled package as it contributes to the checkout `Total`: the
 * breakdown price plus the fees that price leaves out. `getHotelTotalPrice` (and the breakdown
 * `price` it is built from) subtracts each item's `taxesAndFees` and omits property fees; both
 * are only accounted for at the order-level `Total`. Bundled flight tiles and fare upsells
 * display this alongside the flight fare so their package price matches that `Total` rather
 * than the `Hotel + Flights Package` line.
 */
export const getBundledHotelPackagePrice = createSelector(
  (state: App.State) => getHotelTotalPrice(state),
  (state: App.State) => getHotelFeesOnTopOfBreakdownPrice(state),
  (hotelPrice, feesOnTop) => hotelPrice + feesOnTop,
)

/**
 * `getBundledHotelPackagePrice`'s LuxPlus member counterpart, for the "with LuxPlus" row on
 * bundled flight tiles and fare upsells. The hotel can carry a member price of its own, so a
 * member figure that only discounts the flight overstates the package by the hotel's member
 * saving. Mirrors the checkout LuxPlus page, which totals `memberPrice || price` per item
 * (`luxPlus/selectors/benefits/accommodation.ts`).
 *
 * The breakdown row's `memberPrice` folds in `flightTotals.price` - the same figure subtracted
 * back off here - so unlike the regular price there is no member/regular flight basis to
 * reconcile. A cart with no member pricing reports `memberPrice: 0`, and then the member base
 * is just the regular one.
 */
export const getBundledHotelPackageMemberPrice = createSelector(
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => getBundledFlightTotals(state),
  (state: App.State) => getHotelFeesOnTopOfBreakdownPrice(state),
  (state: App.State) => getBundledHotelPackagePrice(state),
  (state: App.State) => getFlightBundledItemIds(state),
  ({ data: { accommodations } }, flightsTotal, feesOnTop, packagePrice, flightBundledItemIds) => {
    const hotelAccommodations = accommodations.filter(isLEContractedHotelBreakdownView)
    const bedbankAccommodations = accommodations.filter(view => isFlightBundledBedbankBreakdownView(view, flightBundledItemIds))
    const memberPriceTotal = sum(hotelAccommodations, view => view.memberPrice ?? 0)
    const bedbankMemberPriceTotal = sum(bedbankAccommodations, view => view.memberPrice ?? 0)

    if (!memberPriceTotal && !bedbankMemberPriceTotal) {
      return packagePrice
    }

    const flightPrice = flightsTotal.data.price
    const hotelMemberPrice = memberPriceTotal ? memberPriceTotal - flightPrice : 0
    // Each flight-bundled bedbank row folds in the whole flight price, as in `getHotelTotalPrice`.
    const bedbankMemberPrice = bedbankMemberPriceTotal - flightPrice * bedbankAccommodations.length

    return hotelMemberPrice + bedbankMemberPrice + feesOnTop
  })

/** Hotel contribution for the current checkout customer's bundle total. */
export const getBundledHotelCheckoutPrice = createSelector(
  (state: App.State) => getBundledHotelPackagePrice(state),
  (state: App.State) => getBundledHotelPackageMemberPrice(state),
  (state: App.State) => getHasLuxPlusOrSubscriptionInCart(state),
  (regularPrice, memberPrice, hasLuxPlus) => hasLuxPlus ? memberPrice : regularPrice,
)

export const getAccommodationOccupantsFromCart = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): App.Occupants | undefined => {
    if (items.length) {
      return items.reduce((acc, item) => {
        if ('occupancy' in item && item.occupancy) {
          return {
            adults: acc.adults + item.occupancy.adults,
            children: acc.children + (item.occupancy.children || 0),
            infants: acc.infants + (item.occupancy.infants || 0),
            teenagers: acc.teenagers + (item.occupancy.teenagers || 0),
          }
        }
        return acc
      }, { adults: 0, children: 0, infants: 0, teenagers: 0 })
    }
  },
)

export const getAccommodationStartDate = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): string | undefined => {
    const startDates = items
      .map(item => {
        if (isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isVillaItem(item) || isAccommodationV2Item(item)) {
          return item.checkIn
        }
        if (isTourV1Item(item) || isTourV2Item(item)) {
          return item.startDate
        }
      }).filter(excludeNullOrUndefined)
    return min(startDates, (date) => new Date(date))
  },
)

export const getAccommodationEndDate = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): string | undefined => {
    const startDates = items
      .map(item => {
        if (isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isVillaItem(item) || isAccommodationV2Item(item)) {
          return item.checkOut
        }
        if (isTourV1Item(item) || isTourV2Item(item)) {
          return item.endDate
        }
      }).filter(excludeNullOrUndefined)

    return max(startDates, (date) => new Date(date))
  },
)

export function getAccommodationFlightDestinationAirportCode(view: App.Checkout.AccommodationOfferView): string | undefined {
  if (isAccommodationV2AccommodationOfferView(view)) {
    for (const itemView of view.itemViews) {
      if (isAccommodationV2Item(itemView.item) && itemView.item.flightDestinationAirportCode) {
        return itemView.item.flightDestinationAirportCode
      }
    }
    return view.property?.airportCode
  }

  if (view.offerType === OFFER_TYPE_BED_BANK) {
    for (const itemView of view.itemViews) {
      if (isBedbankItem(itemView.item) && itemView.item.flightDestinationAirportCode) {
        return itemView.item.flightDestinationAirportCode
      }
    }
    return 'offer' in view && isBedbankOffer(view.offer) ? view.offer.flights.airportCode : undefined
  }

  if ('offer' in view && view.offer && isLEOffer(view.offer)) {
    return view.offer.flightDestinationPort
  }
}

export const areAccommodationFlightDatesWithinBookingWindow = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (views): boolean => {
    const latestDate = moment().add(FLIGHTS_MAX_LEAD_TIME_DAYS, 'days').startOf('day')
    return views.data.every(view =>
      [view.startDate, view.endDate].every(date => !date || moment(date).isSameOrBefore(latestDate, 'day')),
    )
  },
)

export const canShowAddFlightsForAccommodationItems = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getDefaultAirport(state),
  (state: App.State) => getAddFlightsAccommodationItems(state),
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => areAccommodationFlightDatesWithinBookingWindow(state),
  (accommodationViews, defaultUserAirport, addFlightsAccommodationItems, accommodationItems, datesWithinBookingWindow): boolean => {
    if (!datesWithinBookingWindow) return false

    // getAddFlightsAccommodationItems excludes villas until flight bundle functionality is added for villa offers.
    if (!addFlightsAccommodationItems.length) return false

    const flightOccupants = countOccupantsForFlights(accommodationItems.flatMap(item => 'occupancy' in item && item.occupancy ? [item.occupancy] : []))
    const passengerCount = sumUpOccupancies([flightOccupants])
    if (passengerCount > maxFlightPassengerCount) return false

    if (!accommodationViews.data.length) return false

    return accommodationViews.data.every(view => {
      const destinationAirportCode = getAccommodationFlightDestinationAirportCode(view)

      return !!view.startDate &&
        diffInDays(new Date(), new Date(view.startDate)) <= FLIGHTS_MAX_LEAD_TIME_DAYS &&
        !!destinationAirportCode &&
        destinationAirportCode !== defaultUserAirport?.code
    })
  },
)

export const getHasOnlyAuDomesticAccommodationFlightRoute = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getDefaultAirport(state),
  (state: App.State) => state.geo.airports,
  (accommodationViews, defaultUserAirport, airports): boolean => {
    if (!accommodationViews.hasRequiredData || !accommodationViews.data.length) return false

    return accommodationViews.data.every(view => isAuDomesticAirportRoute({
      airports,
      departureAirportCode: defaultUserAirport?.code,
      destinationAirportCode: getAccommodationFlightDestinationAirportCode(view),
    }))
  },
)
